import { faCopy, faDollarSign, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Column, DataTable, DataTableContainer, Filter, TableActionButton } from 'components/DataTable';
import { useApi } from '../../../../hooks/useApi';
import { useConfirmedAction } from 'hooks/useConfirmedAction';
import { useModal } from 'hooks/useModal';
import { useCallback, useRef } from 'react';
import { HypotheticalPriceComponent, SpaceBlockingForm } from '../..';
import { SpaceBlocking } from '../../types';
import { SpaceBlockingDatesComponent } from './SpaceBlockingDatesComponent';
import { useSettings } from 'components/Clubs/hooks';
import { get } from 'lodash';
import { SpaceBlockingCopyWizard } from '../SpaceBlockingCopyWizard';
import { Button } from '@ui';

interface Props {
    clubSlug: string;
}

export const SpaceBlockingList = ({ clubSlug }: Props) => {
    const [setModal, resetModal] = useModal();
    const [settings] = useSettings('space-bookings', clubSlug);
    const showHypotheticalPrice = get(settings, 'view.show_hypothetical_price');
    const confirm = useConfirmedAction({ message: 'Möchtest du diese Blockierung wirklich löschen?' });
    const api = useApi();
    const asyncList = useRef<any>(undefined);

    const handleShowSpaceBlockingForm = useCallback(
        (blocking?: SpaceBlocking) =>
            setModal({
                title: blocking ? `Sperrung "${blocking.reason}" bearbeiten` : 'Sperrung erstellen',
                content: (
                    <SpaceBlockingForm
                        clubSlug={clubSlug}
                        blocking={blocking}
                        callback={() => {
                            asyncList.current?.reload?.();
                            resetModal();
                        }}
                    />
                ),
                show: true
            }),
        []
    );

    const handleCopySpaceBlocking = useCallback(
        (blocking: any) =>
            setModal({
                title: 'Sperrung kopieren',
                content: (
                    <SpaceBlockingCopyWizard
                        blocking={blocking}
                        callback={() => {
                            asyncList.current?.reload?.();
                            resetModal();
                        }}
                    />
                ),
                show: true
            }),
        []
    );

    const handleDeleteSpaceBlocking = useCallback((blocking: SpaceBlocking) => {
        api({
            url: `space/blocking/${blocking.id}`,
            method: 'DELETE',
            onSuccess: () => asyncList.current?.reload?.()
        }).catch(() => {});
    }, []);

    const columns: Column<SpaceBlocking & { spaces_names: string[] }>[] = [
        {
            uid: 'spaces',
            name: 'Plätze',
            allowsSorting: true,
            renderer(_, item) {
                return item.spaces_names?.join(', ');
            }
        },
        {
            uid: 'start',
            name: 'Am',
            allowsSorting: true,
            renderer: (_, item) => <SpaceBlockingDatesComponent blocking={item} />
        },
        {
            uid: 'reason',
            name: 'Begründung',
            allowsSorting: true
        },
        {
            uid: 'actions',
            name: 'Optionen',
            align: 'end'
        }
    ];

    const filters: Filter[] = [
        { filterName: 'Plätze', filterKey: 'spaces', filterType: 'text' },
        { filterName: 'Startdatum', filterKey: 'start', filterType: 'date' },
        { filterName: 'Zeitraum', filterKey: 'range', filterType: 'date' },
        { filterName: 'Rhythmus', filterKey: 'rhythm', filterType: 'text' },
        { filterName: 'Begründung', filterKey: 'reason', filterType: 'text' },
        { filterName: 'Gruppe', filterKey: 'group', filterType: 'text' }
    ];

    return (
        <DataTableContainer>
            <Button className="tw-py-5" onPress={() => handleShowSpaceBlockingForm()}>
                Blockierung erstellen
            </Button>
            <DataTable
                className="tw-flex-auto"
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false
                }}
                hideSearchBar
                asyncListRef={asyncList}
                columns={columns}
                filters={filters}
                fetchUrl={`space/blocking/${clubSlug}`}
                rowActions={(item) => (
                    <TableActionButton.Container justify="end">
                        {showHypotheticalPrice && (
                            <TableActionButton
                                tooltipProps={{ placement: 'leftStart', hideArrow: true }}
                                tooltip={<HypotheticalPriceComponent blockingId={item.id} />}
                                icon={faDollarSign}
                            />
                        )}
                        <TableActionButton icon={faCopy} onPress={() => handleCopySpaceBlocking(item)} />
                        <TableActionButton icon={faEdit} onPress={() => handleShowSpaceBlockingForm(item)} />
                        <TableActionButton
                            icon={faTrash}
                            onPress={() => confirm(() => handleDeleteSpaceBlocking(item))}
                        />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default SpaceBlockingList;
